
import { useState, useMemo } from 'react';
import { TaskFilters, TaskWithLead } from '@/types/taskFilters';
import { useDebounce } from './useDebounce';
import { addDays, isWithinInterval, startOfDay, endOfDay } from 'date-fns';

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

export const useTaskFilters = (tasks: TaskWithLead[], currentUserId: string) => {
  const [filters, setFilters] = useState<TaskFilters>({
    search: '',
    priority: 'all',
    dueDate: 'all',
    userId: 'my-tasks',
    categories: [],
    sortBy: 'dueDate',
    sortOrder: 'asc',
  });

  const debouncedSearch = useDebounce(filters.search, 300);

  const updateFilters = (newFilters: Partial<TaskFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      priority: 'all',
      dueDate: 'all',
      userId: 'my-tasks',
      categories: [],
      sortBy: 'dueDate',
      sortOrder: 'asc',
    });
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // User filter
      if (filters.userId === 'my-tasks') {
        if (task.assignedTo !== currentUserId) return false;
      } else if (filters.userId) {
        if (task.assignedTo !== filters.userId) return false;
      }

      // Search filter
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const matchesSearch = 
          task.title.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.leads.business_name.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Priority filter
      if (filters.priority !== 'all' && task.priority !== filters.priority) {
        return false;
      }

      // Categories filter
      if (filters.categories.length > 0) {
        const taskCategories = task.categories || [];
        const hasMatchingCategory = filters.categories.some(category => 
          taskCategories.includes(category)
        );
        
        if (!hasMatchingCategory) return false;
      }

      // Due date filter
      if (filters.dueDate !== 'all' && task.dueDate) {
        const today = startOfDay(new Date());
        const taskDate = startOfDay(new Date(task.dueDate));

        switch (filters.dueDate) {
          case 'today':
            if (!isWithinInterval(taskDate, {
              start: startOfDay(today),
              end: endOfDay(today)
            })) return false;
            break;
          case 'tomorrow':
            const tomorrow = addDays(today, 1);
            if (!isWithinInterval(taskDate, {
              start: startOfDay(tomorrow),
              end: endOfDay(tomorrow)
            })) return false;
            break;
          case 'week':
            const endOfWeek = addDays(today, 7);
            if (!isWithinInterval(taskDate, {
              start: today,
              end: endOfWeek
            })) return false;
            break;
          case 'month':
            const endOfMonth = addDays(today, 30);
            if (!isWithinInterval(taskDate, {
              start: today,
              end: endOfMonth
            })) return false;
            break;
        }
      }

      return true;
    });
  }, [tasks, debouncedSearch, filters.priority, filters.dueDate, filters.userId, filters.categories, currentUserId]);

  // Sort tasks
  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks].sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'dueDate':
          const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          comparison = dateA - dateB;
          break;
        case 'priority':
          comparison = (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'assignee':
          const nameA = a.assignee ? `${a.assignee.first_name} ${a.assignee.last_name}` : 'zzz';
          const nameB = b.assignee ? `${b.assignee.first_name} ${b.assignee.last_name}` : 'zzz';
          comparison = nameA.localeCompare(nameB);
          break;
      }

      return filters.sortOrder === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }, [filteredTasks, filters.sortBy, filters.sortOrder]);

  const groupedTasks = useMemo(() => {
    const now = new Date();
    
    return {
      pending: sortedTasks.filter(
        task => task.status === 'pending' && (!task.dueDate || new Date(task.dueDate) >= now)
      ),
      inProgress: sortedTasks.filter(
        task => task.status === 'in_progress'
      ),
      overdue: sortedTasks.filter(
        task => task.status === 'pending' && task.dueDate && new Date(task.dueDate) < now
      ),
      completed: sortedTasks.filter(
        task => task.status === 'completed'
      )
    };
  }, [sortedTasks]);

  return {
    filters,
    updateFilters,
    resetFilters,
    groupedTasks
  };
};
